/**
 * Upgrade dialog shown when a discovered asset requires a paid tier to scan
 * (scan_capability.reason === "tier_upgrade_required").
 *
 * Wires to the existing Stripe checkout flow via `useCreateCheckout`. The
 * target tier is configurable; it defaults to the first paid plan returned by
 * the billing API that isn't the current plan.
 */

import { useMemo } from "react";
import { Loader2, Lock, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePlans, useCreateCheckout } from "@/hooks/useBilling";

interface AwsDiscoveryUpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Asset name for context in the dialog copy. */
  assetName?: string;
}

export function AwsDiscoveryUpgradeDialog({
  open,
  onOpenChange,
  assetName,
}: AwsDiscoveryUpgradeDialogProps) {
  const { data: plans } = usePlans();
  const createCheckout = useCreateCheckout();

  // Pick the first paid plan that isn't the current one as the upgrade target.
  const targetTier = useMemo(() => {
    if (!plans) return null;
    const paid = plans.filter((p) => !p.is_current && (p.price_amount ?? 0) > 0);
    return paid[0] ?? null;
  }, [plans]);

  const handleUpgrade = () => {
    if (!targetTier) return;
    const successUrl = `${window.location.origin}/ai-inventory?upgraded=1`;
    const cancelUrl = `${window.location.origin}/ai-inventory`;
    createCheckout.mutate({
      tierName: targetTier.name,
      successUrl,
      cancelUrl,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
              <Lock className="h-4 w-4 text-primary" />
            </div>
            <DialogTitle>Upgrade to run security scans</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {assetName ? (
              <>
                Scanning <span className="font-medium text-foreground">{assetName}</span> for
                vulnerabilities is a paid feature. Free discovery finds your AI; a paid plan
                lets you attack it.
              </>
            ) : (
              <>
                Running a security scan against this endpoint is a paid feature. Free discovery
                finds your AI; a paid plan lets you attack it.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {targetTier && (
          <div className="rounded-lg border bg-muted/30 p-4 text-sm">
            <p className="font-medium">{targetTier.name}</p>
            {targetTier.price_amount != null && (
              <p className="text-muted-foreground">
                ${(targetTier.price_amount / 100).toFixed(0)}
                {targetTier.price_interval === "YEAR" ? "/yr" : "/mo"}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Not now
          </Button>
          <Button
            onClick={handleUpgrade}
            disabled={!targetTier || createCheckout.isPending}
          >
            {createCheckout.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Redirecting…
              </>
            ) : (
              <>
                Upgrade
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
